import type { TerminalServerMessage } from '@this+that/models';
import log from 'loglevel';

import { createListeners } from './createListeners';
import { createLocalTransport, probeLocalDaemon } from './localTransport';
import { createNoneTransport } from './noneTransport';
import { createRelayTransport } from './relayTransport';
import {
  type ResolvedTerminalDevice,
  resolveTerminalDevice,
} from './terminalDevices';
import type {
  TerminalDevice,
  TerminalGateway,
  TerminalTransport,
} from './types';
import {
  TerminalConnectionStatus,
  TerminalTransportKind,
} from '../../../enums/terminal';

export function createFallbackTransport(
  primary: TerminalTransport,
  createSecondary: () => TerminalTransport,
): TerminalTransport {
  const messages = createListeners<TerminalServerMessage>();
  const statuses = createListeners<TerminalConnectionStatus>();
  let current = primary;
  let isClosed = false;

  const wire = (transport: TerminalTransport, canFallBack: boolean) => {
    transport.onMessage(messages.emit);
    transport.onStatus((status) => {
      if (
        canFallBack &&
        status === TerminalConnectionStatus.OFFLINE &&
        !isClosed
      ) {
        log.info('terminal: local transport offline, switching to relay');
        transport.close();
        current = createSecondary();
        wire(current, false);
        current.connect();
        return;
      }
      statuses.emit(status);
    });
  };

  return {
    connect: () => {
      wire(primary, true);
      primary.connect();
    },
    send: (message) => current.send(message),
    onMessage: messages.subscribe,
    onStatus: statuses.subscribe,
    getKind: () => current.getKind(),
    getDeviceId: () => current.getDeviceId?.() ?? null,
    close: () => {
      isClosed = true;
      current.close();
      messages.clear();
      statuses.clear();
    },
  };
}

async function createAutoTransport(
  gateway: TerminalGateway,
  { device, isExplicit }: ResolvedTerminalDevice & { device: TerminalDevice },
): Promise<TerminalTransport> {
  const relay = () => createRelayTransport(gateway, device);
  // The daemon ping can't say which device answered, so a chosen one skips it.
  if (isExplicit) {
    log.info(
      `terminal: transport relay for ${device.name} (chosen device, local probe skipped)`,
    );
    return relay();
  }
  const local = () => createLocalTransport(gateway, device);
  if (await probeLocalDaemon()) {
    log.info(
      `terminal: transport local for ${device.name} (ping ok), relay as fallback`,
    );
    return createFallbackTransport(local(), relay);
  }
  log.info(`terminal: transport relay for ${device.name} (local ping failed)`);
  return relay();
}

export async function createDeviceTransport(
  kind: TerminalTransportKind,
  gateway: TerminalGateway,
  preferredDeviceId: string | null,
): Promise<TerminalTransport> {
  const resolved = resolveTerminalDevice(
    await gateway.listDevices(),
    preferredDeviceId,
  );
  const { device } = resolved;
  if (!device) {
    log.info('terminal: no paired device, showing the no-daemon state');
    return createNoneTransport();
  }
  if (kind === TerminalTransportKind.RELAY) {
    log.info(`terminal: transport relay (forced) for ${device.name}`);
    return createRelayTransport(gateway, device);
  }
  if (kind === TerminalTransportKind.LOCAL) {
    log.info(`terminal: transport local (forced) for ${device.name}`);
    return createLocalTransport(gateway, device);
  }
  return createAutoTransport(gateway, { ...resolved, device });
}
